import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import SectionCard from '../../../components/SectionCard';
import StatCard from '../../../components/StatCard';
import StatusChip from '../../../components/StatusChip';
import InvButton from '../../../components/InvButton';
import { InvTextField } from '../../../components/InvField';
import {
  getControlledQueue,
  authorizeControlledItem,
  denyControlledItem,
} from '../../../api/pharmacy';
import { mapList, mapControlledItem } from '../../../utils/pharmacyMappers';
import { isControlledAuthPending } from '../../../utils/controlledDrug';
import { getPharmacyActorId, getPharmacyActorName } from '../../../hooks/usePharmacyRole';
import usePharmacyLoad from '../../../hooks/usePharmacyLoad';
import useRefreshOnFocus from '../../../hooks/useRefreshOnFocus';
import { formatDateTime } from '../../../utils';
import { isUuidLike } from '../../../utils/itemDisplay';
import { IPD_COLORS, cardSx } from '../../../theme';
import { fadeInSx } from '../pharmacyStyles';
import PharmacyTableState from '../PharmacyTableState';

export default function ControlledQueueTab() {
  const history = useHistory();
  // resume_path is relative to the inventory suite root, and this tab lives at
  // <suite>/pharmacy/controlled-queue.
  const { url } = useRouteMatch();
  const suiteRoot = url.replace(/\/pharmacy\/controlled-queue\/?$/, '');
  const [dialog, setDialog] = useState(null);
  const [notes, setNotes] = useState('');
  const [actioning, setActioning] = useState(false);

  // Unfiltered on purpose. Fetching only PENDING meant a row vanished the instant it
  // was authorized — taking its link to the order with it, which is exactly when
  // the pharmacist needs it. It also left the "Authorized" stat card permanently 0,
  // since no authorized row was ever loaded to count.
  const { data, loading, error, reload } = usePharmacyLoad(
    // status: undefined is load-bearing -- getControlledQueue defaults to
    // PENDING, so calling it bare would silently re-apply the very filter
    // this comment says was removed.
    () => getControlledQueue({ status: undefined }),
    [],
  );

  const items = useMemo(
    () => mapList(data, mapControlledItem)
      .slice()
      .sort((a, b) => String(b.requested_at || '').localeCompare(String(a.requested_at || ''))),
    [data],
  );
  const pending = items.filter((i) => isControlledAuthPending(i.status)).length;

  // Refresh when the pharmacist comes back to this tab, and on the Refresh
  // button — not on a timer. A counter no longer stands waiting on the till
  // (the order is parked), so a background poll would buy nothing.
  useRefreshOnFocus(reload);

  // Announce arrivals, so a pharmacist who glanced away still notices.
  const seenPendingRef = useRef(null);
  useEffect(() => {
    const pendingIds = items.filter((i) => isControlledAuthPending(i.status)).map((i) => String(i.id));
    const seen = seenPendingRef.current;
    seenPendingRef.current = new Set(pendingIds);
    // First load establishes the baseline rather than announcing the backlog.
    if (seen === null) return;
    const arrived = pendingIds.filter((id) => !seen.has(id));
    if (arrived.length === 1) {
      const row = items.find((i) => String(i.id) === arrived[0]);
      toast.info(`New authorization request: ${row?.medicine || 'controlled medicine'}`);
    } else if (arrived.length > 1) {
      toast.info(`${arrived.length} new controlled authorization requests`);
    }
  }, [items]);

  function openSource(row) {
    if (!row.transaction_id) return;
    history.push(`${suiteRoot}/pharmacy/controlled-orders/${row.transaction_id}`);
  }

  function closeDialog() {
    if (actioning) return;
    setDialog(null);
    setNotes('');
  }

  async function handleAction(approve) {
    if (!dialog) return;
    if (!approve && !notes.trim()) {
      toast.error('A reason is required to deny authorization');
      return;
    }

    setActioning(true);
    try {
      const authId = dialog.id;
      const scope = {
        tenant_id: dialog._raw?.tenant_id,
        hospital_id: dialog._raw?.hospital_id,
      };

      // No supervisor_id / supervisor_name here on purpose. The gateway stamps
      // both from the caller's JWT; anything sent from the browser would be
      // overwritten anyway, and an approver a pharmacist can type is one they
      // can forge.
      if (approve) {
        await authorizeControlledItem(authId, {
          notes: notes.trim() || 'Verified prescription',
        }, scope);
      } else {
        await denyControlledItem(authId, {
          reason: notes.trim() || 'Authorization denied',
        }, scope);
      }
      toast.success(approve ? 'Controlled medicine authorized' : 'Authorization denied');
      closeDialog();
      reload();
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    } finally {
      setActioning(false);
    }
  }

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard label="Awaiting Authorization" value={pending} icon={LockIcon} tone="amber" loading={loading} />
        <StatCard label="Authorized" value={items.filter((i) => i.status === 'AUTHORIZED').length} icon={LockIcon} tone="mint" loading={loading} />
        <StatCard label="Total in Queue" value={items.length} icon={LockIcon} tone="teal" loading={loading} />
      </Box>

      <SectionCard
        title="Controlled Substance Queue"
        subtitle="Every controlled / narcotic line — counter sales and IPD dispenses alike — is authorized or denied here, and nowhere else"
        actions={(
          <InvButton
            size="small"
            variant="outlined"
            tone="teal"
            startIcon={<RefreshIcon />}
            disabled={loading}
            onClick={reload}
          >
            Refresh
          </InvButton>
        )}
      >
        <PharmacyTableState
          loading={loading}
          error={error}
          empty={!loading && !error && items.length === 0}
          emptyMessage="Nothing waiting for authorization. New requests appear here automatically."
          onRetry={reload}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                  {['Medicine', 'Patient', 'Qty', 'Source','Requested By', 'Requested', 'Status', 'Actions'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, color: IPD_COLORS.textSecondary, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                    <td style={{ padding: '12px', fontWeight: 600 }}>{row.medicine || '—'}</td>
                    <td style={{ padding: '12px' }}>
                      <Box sx={{ fontWeight: 600 }}>
                        {row.patient_name || row.order_number || 'Not registered'}
                      </Box>
                      {row.patient_mobile && (
                        <Box sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          {row.patient_mobile}
                        </Box>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>{row.qty}</td>
                    <td style={{ padding: '12px', fontSize: 13 }}>
                      <Box>{row.source_type === 'SALE' ? 'Counter sale' : 'IPD dispense'}</Box>
                      {row.source_number && (
                        <Box sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, fontFamily: 'monospace' }}>
                          {row.source_number}
                        </Box>
                      )}
                    </td>
                    {/* <td style={{ padding: '12px', fontSize: 13, maxWidth: 160 }}>{row.reason || '—'}</td> */}
                    <td style={{ padding: '12px', fontSize: 13 }}>{displayRequestedBy(row.requested_by)}</td>
                    <td style={{ padding: '12px', fontSize: 13 }}>{formatDateTime(row.requested_at)}</td>
                    <td style={{ padding: '12px' }}><StatusChip status={row.status} /></td>
                    <td style={{ padding: '12px' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {row.transaction_id && (
                          <Tooltip title="Open the order this request came from">
                            <IconButton size="small" sx={{ color: IPD_COLORS.textSecondary }}
                              onClick={() => openSource(row)}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isControlledAuthPending(row.status) && (
                          <>
                            <Tooltip title="Authorize">
                              <IconButton size="small" sx={{ color: IPD_COLORS.primary }}
                                onClick={() => { setNotes(''); setDialog({ ...row, action: 'approve' }); }}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Deny">
                              <IconButton size="small" sx={{ color: IPD_COLORS.error.text }}
                                onClick={() => { setNotes(''); setDialog({ ...row, action: 'reject' }); }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </PharmacyTableState>
      </SectionCard>

      <Dialog
        open={Boolean(dialog)}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>
            {dialog?.action === 'approve' ? 'Authorize Controlled Medicine' : 'Deny Authorization'}
          </Typography>
          <IconButton size="small" onClick={closeDialog} disabled={actioning} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
            {dialog
              ? `${dialog.action === 'approve' ? 'Authorize' : 'Deny'} dispensing of ${dialog.medicine}?`
              : ''}
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: '8px',
              border: `1px solid ${IPD_COLORS.border}`,
              backgroundColor: IPD_COLORS.panelHeaderBg,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: IPD_COLORS.textSecondary }}>
              Authorizing as
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
              {getPharmacyActorName()}
            </Typography>
            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
              Recorded against your signed-in account.
            </Typography>
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <InvTextField
              label={dialog?.action === 'approve' ? 'Notes (optional)' : 'Reason *'}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={actioning}
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" disabled={actioning} onClick={closeDialog}>
            Cancel
          </InvButton>
          <InvButton
            loading={actioning}
            disabled={dialog?.action === 'reject' && !notes.trim()}
            tone={dialog?.action === 'reject' ? 'red' : undefined}
            onClick={() => handleAction(dialog?.action === 'approve')}
          >
            {dialog?.action === 'approve' ? 'Authorize' : 'Deny'}
          </InvButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function displayRequestedBy(value) {
  if (!value) return '—';
  const text = String(value).trim();
  if (!text) return '—';
  if (!isUuidLike(text)) return text;
  if (String(text) === String(getPharmacyActorId())) return getPharmacyActorName();
  return 'Counter staff';
}
